package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminApiClient
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.*

@Composable
fun AutomationTestsScreen(apiClient: AdminApiClient) {
    // Inject spin keyframes for loading spinner
    LaunchedEffect(Unit) {
        val style = kotlinx.browser.document.createElement("style")
        style.textContent = "@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }"
        kotlinx.browser.document.head?.appendChild(style)
    }

    val colors = AppTheme.colors
    val isDark = AppTheme.isDark
    val scope = rememberCoroutineScope()

    // Auto-detect environment from hostname
    val environment = when (kotlinx.browser.window.location.hostname) {
        "scora.twistmena.com" -> "production"
        else -> "staging"
    }
    var selectedSuites by remember { mutableStateOf(setOf("auth", "onboarding", "favorites", "search_legacy", "catalog_fixtures", "notifications_devices")) }
    var triggerMessage by remember { mutableStateOf<String?>(null) }
    var isTriggering by remember { mutableStateOf(false) }

    // Run history
    var historyState by remember { mutableStateOf<UiState<OffsetPage<AutomationTestRunResponse>>>(UiState.Loading) }
    var historyOffset by remember { mutableStateOf(0) }
    var refreshKey by remember { mutableStateOf(0) }

    // Active run detail
    var activeRunId by remember { mutableStateOf<String?>(null) }
    var activeRunDetail by remember { mutableStateOf<AutomationTestRunDetailResponse?>(null) }
    var expandedSuites by remember { mutableStateOf(setOf<String>()) }
    var expandedTests by remember { mutableStateOf(setOf<String>()) }

    // Load history
    LaunchedEffect(historyOffset, refreshKey) {
        try {
            val page = apiClient.getTestRuns(historyOffset, 20)
            historyState = UiState.Success(page)
        } catch (e: Exception) {
            historyState = UiState.Error("Failed to load test runs")
        }
    }

    // Poll active run
    LaunchedEffect(activeRunId) {
        val runId = activeRunId ?: return@LaunchedEffect
        while (true) {
            try {
                val detail = apiClient.getTestRunDetail(runId)
                activeRunDetail = detail
                if (detail.run.status != "running") break
            } catch (_: Exception) {}
            delay(2000)
        }
        refreshKey++
    }

    val allSuites = listOf(
        "auth" to "Authentication",
        "onboarding" to "Onboarding",
        "favorites" to "Favorites",
        "search_legacy" to "Search & Legacy",
        "catalog_fixtures" to "Catalog & Fixtures",
        "notifications_devices" to "Notifications & Devices"
    )

    // Main layout: two columns
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(Spacing.xl)
            property("min-height", "calc(100vh - 140px)")
        }
    }) {
        // Left panel — Controls + History
        Div({
            style {
                width(320.px)
                property("flex-shrink", "0")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.lg)
            }
        }) {
            // Suite selector
            Div({
                style {
                    padding(Spacing.lg)
                    backgroundColor(Color(colors.surface))
                    borderRadius(BorderRadius.lg)
                    property("border", "1px solid ${colors.border}")
                }
            }) {
                Div({
                    style {
                        marginBottom(Spacing.sm)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Span({ style { fontWeight("600"); color(Color(colors.text)); fontSize(Typography.sm) } }) { Text("Test Suites") }
                    Button({
                        onClick {
                            selectedSuites = if (selectedSuites.size == allSuites.size) emptySet()
                            else allSuites.map { it.first }.toSet()
                        }
                        style {
                            fontSize(Typography.xs)
                            color(Color(colors.primary))
                            backgroundColor(Color("transparent"))
                            border(0.px)
                            cursor("pointer")
                            fontWeight("600")
                        }
                    }) { Text(if (selectedSuites.size == allSuites.size) "Deselect All" else "Select All") }
                }
                for ((key, label) in allSuites) {
                    Div({
                        onClick { selectedSuites = if (key in selectedSuites) selectedSuites - key else selectedSuites + key }
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                            padding(Spacing.xs, 0.px)
                            cursor("pointer")
                            color(Color(colors.text))
                            fontSize(Typography.sm)
                        }
                    }) {
                        Div({
                            style {
                                width(16.px)
                                height(16.px)
                                borderRadius(BorderRadius.sm)
                                property("border", "2px solid ${if (key in selectedSuites) colors.primary else colors.border}")
                                backgroundColor(Color(if (key in selectedSuites) colors.primary else "transparent"))
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                property("transition", "all 0.15s ease")
                            }
                        }) {
                            if (key in selectedSuites) {
                                Span({ style { color(Color("#fff")); fontSize(Typography.xs); fontWeight("700") } }) { Text("\u2713") }
                            }
                        }
                        Text(label)
                    }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                }
            }) {
                Button({
                    if (!isTriggering && selectedSuites.isNotEmpty()) {
                        onClick {
                            isTriggering = true
                            triggerMessage = null
                            scope.launch {
                                try {
                                    val resp = apiClient.triggerTestRun(TriggerTestRunRequest(environment, selectedSuites.toList()))
                                    activeRunId = resp.runId
                                    activeRunDetail = null
                                    expandedSuites = emptySet()
                                    expandedTests = emptySet()
                                    triggerMessage = "Tests started"
                                } catch (e: Exception) {
                                    triggerMessage = "Failed: ${e.message}"
                                } finally {
                                    isTriggering = false
                                }
                            }
                        }
                    }
                    style {
                        primaryButtonStyle(colors, disabled = isTriggering || selectedSuites.isEmpty())
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.sm)
                        flex(1)
                    }
                }) { Text(if (isTriggering) "Starting..." else "Run Selected") }

                Button({
                    if (!isTriggering) {
                        onClick {
                            selectedSuites = allSuites.map { it.first }.toSet()
                            isTriggering = true
                            triggerMessage = null
                            scope.launch {
                                try {
                                    val resp = apiClient.triggerTestRun(TriggerTestRunRequest(environment, allSuites.map { it.first }))
                                    activeRunId = resp.runId
                                    activeRunDetail = null
                                    expandedSuites = emptySet()
                                    expandedTests = emptySet()
                                    triggerMessage = "All tests started"
                                } catch (e: Exception) {
                                    triggerMessage = "Failed: ${e.message}"
                                } finally {
                                    isTriggering = false
                                }
                            }
                        }
                    }
                    style {
                        secondaryButtonStyle(colors)
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.sm)
                        flex(1)
                    }
                }) { Text("Run All") }
            }

            if (triggerMessage != null) {
                Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); padding(Spacing.xs, 0.px) } }) {
                    Text(triggerMessage!!)
                }
            }

            // Run history list
            Div({
                style {
                    padding(Spacing.lg)
                    backgroundColor(Color(colors.surface))
                    borderRadius(BorderRadius.lg)
                    property("border", "1px solid ${colors.border}")
                    flex(1)
                    property("overflow-y", "auto")
                }
            }) {
                Div({ style { marginBottom(Spacing.sm); fontWeight("600"); color(Color(colors.text)); fontSize(Typography.sm) } }) {
                    Text("Run History")
                }

                when (val s = historyState) {
                    is UiState.Loading -> Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text("Loading...") }
                    is UiState.Error -> Div({ style { color(Color(colors.danger)); fontSize(Typography.sm) } }) { Text(s.message) }
                    is UiState.Success -> {
                        if (s.data.data.isEmpty()) {
                            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text("No runs yet") }
                        }
                        for (run in s.data.data) {
                            val isActive = activeRunId == run.id
                            Div({
                                onClick {
                                    activeRunId = run.id
                                    activeRunDetail = null
                                    expandedSuites = emptySet()
                                    expandedTests = emptySet()
                                }
                                style {
                                    padding(Spacing.sm, Spacing.md)
                                    marginBottom(Spacing.xs)
                                    borderRadius(BorderRadius.md)
                                    cursor("pointer")
                                    backgroundColor(Color(if (isActive) colors.sidebarActive else "transparent"))
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(Spacing.sm)
                                    property("transition", "background-color 0.15s ease")
                                }
                            }) {
                                // Status icon
                                StatusIcon(run.status, isDark)
                                Div({ style { flex(1); property("min-width", "0") } }) {
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.text)); fontWeight("500") } }) {
                                        Text(run.environment.replaceFirstChar { it.uppercase() })
                                    }
                                    Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                        Text(run.startedAtUtc.replace("T", " ").take(19))
                                    }
                                }
                                if (run.totalTests > 0) {
                                    Span({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) {
                                        Text("${run.passedTests}/${run.totalTests}")
                                    }
                                }
                            }
                        }

                        // Pagination
                        if (s.data.total > 20) {
                            Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); marginTop(Spacing.sm); justifyContent(JustifyContent.Center) } }) {
                                Button({
                                    if (historyOffset > 0) onClick { historyOffset -= 20 }
                                    style { paginationButtonStyle(colors, historyOffset <= 0); fontSize(Typography.xs); padding(Spacing.xs, Spacing.sm) }
                                }) { Text("\u25C0") }
                                Button({
                                    if (historyOffset + 20 < s.data.total) onClick { historyOffset += 20 }
                                    style { paginationButtonStyle(colors, historyOffset + 20 >= s.data.total); fontSize(Typography.xs); padding(Spacing.xs, Spacing.sm) }
                                }) { Text("\u25B6") }
                            }
                        }
                    }
                }
            }
        }

        // Right panel — IDE-style test results
        Div({
            style {
                flex(1)
                property("min-width", "0")
            }
        }) {
            val detail = activeRunDetail
            if (detail == null && activeRunId != null) {
                // Loading detail
                Div({
                    style {
                        padding(Spacing.xxl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) { Text("Loading test results...") }
            } else if (detail == null) {
                // No run selected
                Div({
                    style {
                        padding(Spacing.xxl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        property("min-height", "400px")
                    }
                }) {
                    Div({ style { fontSize(40.px); marginBottom(Spacing.lg) } }) { Text("\u25B6") }
                    Div({ style { fontSize(Typography.lg); fontWeight("500") } }) { Text("Select a run or start a new one") }
                    Div({ style { fontSize(Typography.sm); marginTop(Spacing.sm) } }) { Text("Choose test suites and click Run") }
                }
            } else {
                // Run detail header
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        property("border", "1px solid ${colors.border}")
                        marginBottom(Spacing.lg)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.lg)
                    }
                }) {
                    StatusIcon(detail.run.status, isDark, large = true)
                    Div({ style { flex(1) } }) {
                        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.sm) } }) {
                            Span({ style { fontWeight("600"); fontSize(Typography.lg); color(Color(colors.text)) } }) {
                                Text("Test Run")
                            }
                            EnvironmentBadge(detail.run.environment, isDark)
                            StatusBadge(detail.run.status, isDark)
                        }
                        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginTop(Spacing.xs) } }) {
                            Text("Started: ${detail.run.startedAtUtc.replace("T", " ").take(19)}")
                            detail.run.triggeredBy?.let { Text(" \u2022 By: $it") }
                        }
                    }
                    if (detail.run.totalTests > 0) {
                        Div({ style { textAlign("right") } }) {
                            Div({ style { fontSize(Typography.xxl); fontWeight("700"); color(Color(colors.text)) } }) {
                                Text("${detail.run.passedTests}/${detail.run.totalTests}")
                            }
                            Div({ style { fontSize(Typography.xs); color(Color(colors.textMuted)) } }) { Text("tests passed") }
                        }
                    }
                }

                // Test results tree
                val resultsBySuite = detail.results.groupBy { it.suiteName }
                val requestedSuites = detail.run.suites.split(",")
                val suiteLabels = mapOf(
                    "auth" to "Authentication",
                    "onboarding" to "Onboarding",
                    "favorites" to "Favorites",
                    "search_legacy" to "Search & Legacy",
                    "catalog_fixtures" to "Catalog & Fixtures",
                    "notifications_devices" to "Notifications & Devices"
                )

                Div({
                    style {
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        property("border", "1px solid ${colors.border}")
                        property("overflow", "hidden")
                    }
                }) {
                    for (suiteKey in requestedSuites) {
                        val suiteResults = resultsBySuite[suiteKey] ?: emptyList()
                        val suiteLabel = suiteLabels[suiteKey] ?: suiteKey
                        val isExpanded = suiteKey in expandedSuites
                        val suitePassed = suiteResults.count { it.status == "passed" }
                        val suiteFailed = suiteResults.count { it.status == "failed" }
                        val suiteTotal = suiteResults.size
                        val isRunning = detail.run.status == "running" && suiteResults.any { it.status != "passed" && it.status != "failed" && it.status != "skipped" } || (detail.run.status == "running" && suiteResults.isEmpty())
                        val suiteStatus = when {
                            isRunning || (detail.run.status == "running" && suiteResults.isEmpty()) -> "running"
                            suiteFailed > 0 -> "failed"
                            suiteTotal > 0 && suitePassed == suiteTotal -> "passed"
                            suiteResults.isEmpty() && detail.run.status != "running" -> "passed"
                            else -> "running"
                        }

                        // Suite header row
                        Div({
                            onClick { expandedSuites = if (isExpanded) expandedSuites - suiteKey else expandedSuites + suiteKey }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(Spacing.sm)
                                padding(Spacing.md, Spacing.lg)
                                cursor("pointer")
                                property("border-bottom", "1px solid ${colors.border}")
                                backgroundColor(Color(if (isExpanded) colors.surfaceHover else "transparent"))
                                property("transition", "background-color 0.15s ease")
                            }
                        }) {
                            // Expand/collapse arrow
                            Span({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); width(12.px) } }) {
                                Text(if (isExpanded) "\u25BC" else "\u25B6")
                            }
                            StatusIcon(suiteStatus, isDark)
                            Span({ style { fontWeight("600"); fontSize(Typography.base); color(Color(colors.text)); flex(1) } }) {
                                Text(suiteLabel)
                            }
                            if (suiteTotal > 0) {
                                Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                    Text("$suitePassed/$suiteTotal passed")
                                    if (suiteFailed > 0) Text(", $suiteFailed failed")
                                }
                            } else if (detail.run.status == "running") {
                                Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) { Text("pending") }
                            }
                        }

                        // Individual test rows (when expanded)
                        if (isExpanded) {
                            for (result in suiteResults) {
                                val testKey = "${result.suiteName}:${result.testName}"
                                val isTestExpanded = testKey in expandedTests

                                Div({
                                    onClick {
                                        if (result.status == "failed") {
                                            expandedTests = if (isTestExpanded) expandedTests - testKey else expandedTests + testKey
                                        }
                                    }
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(Spacing.sm)
                                        padding(Spacing.sm, Spacing.lg, Spacing.sm, 48.px)
                                        property("border-bottom", "1px solid ${colors.border}")
                                        cursor(if (result.status == "failed") "pointer" else "default")
                                        backgroundColor(Color("transparent"))
                                    }
                                }) {
                                    StatusIcon(result.status, isDark)
                                    Span({ style { fontSize(Typography.sm); color(Color(colors.text)); flex(1) } }) {
                                        Text(result.testName)
                                    }
                                    if (result.durationMs > 0) {
                                        Span({ style { fontSize(Typography.xs); color(Color(colors.textMuted)); property("min-width", "50px"); textAlign("right") } }) {
                                            Text("${result.durationMs}ms")
                                        }
                                    }
                                    if (result.responseCode != null) {
                                        val codeColor = if (result.responseCode!! in 200..299) {
                                            if (isDark) "#86efac" else "#065f46"
                                        } else {
                                            if (isDark) "#fca5a5" else "#b91c1c"
                                        }
                                        Span({ style { fontSize(Typography.xs); color(Color(codeColor)); fontWeight("600") } }) {
                                            Text("${result.responseCode}")
                                        }
                                    }
                                }

                                // Expanded error details
                                if (isTestExpanded && result.errorMessage != null) {
                                    Div({
                                        style {
                                            padding(Spacing.md, Spacing.lg, Spacing.md, 48.px)
                                            backgroundColor(Color(if (isDark) "#1a0000" else "#fff5f5"))
                                            property("border-bottom", "1px solid ${colors.border}")
                                            fontSize(Typography.sm)
                                        }
                                    }) {
                                        if (result.requestInfo != null) {
                                            Div({ style { color(Color(colors.textMuted)); marginBottom(Spacing.xs) } }) {
                                                Span({ style { fontWeight("600") } }) { Text("Request: ") }
                                                Text(result.requestInfo!!)
                                            }
                                        }
                                        Div({ style { color(Color(if (isDark) "#fca5a5" else "#b91c1c")) } }) {
                                            Span({ style { fontWeight("600") } }) { Text("Error: ") }
                                            Text(result.errorMessage!!)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatusIcon(status: String, isDark: Boolean, large: Boolean = false) {
    val size = if (large) 22.px else 14.px

    when (status) {
        "passed" -> {
            // Green filled circle with white checkmark
            Div({
                style {
                    width(size)
                    height(size)
                    borderRadius(100.px)
                    backgroundColor(Color(if (isDark) "#22c55e" else "#16a34a"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("flex-shrink", "0")
                }
            }) {
                Span({ style { color(Color("#fff")); fontSize(if (large) 13.px else 9.px); fontWeight("700"); property("line-height", "1") } }) { Text("\u2713") }
            }
        }
        "failed" -> {
            // Red filled circle with white X
            Div({
                style {
                    width(size)
                    height(size)
                    borderRadius(100.px)
                    backgroundColor(Color(if (isDark) "#ef4444" else "#dc2626"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("flex-shrink", "0")
                }
            }) {
                Span({ style { color(Color("#fff")); fontSize(if (large) 11.px else 8.px); fontWeight("700"); property("line-height", "1") } }) { Text("\u2715") }
            }
        }
        "running" -> {
            // Spinning ring
            Div({
                style {
                    width(size)
                    height(size)
                    borderRadius(100.px)
                    property("border", "${if (large) 3 else 2}px solid ${if (isDark) "#374151" else "#e5e7eb"}")
                    property("border-top-color", if (isDark) "#fbbf24" else "#d97706")
                    property("animation", "spin 0.8s linear infinite")
                    property("flex-shrink", "0")
                }
            })
        }
        else -> {
            // Gray hollow circle (pending/skipped)
            Div({
                style {
                    width(size)
                    height(size)
                    borderRadius(100.px)
                    property("border", "${if (large) 2 else 2}px solid ${if (isDark) "#4b5563" else "#d1d5db"}")
                    backgroundColor(Color("transparent"))
                    property("flex-shrink", "0")
                }
            })
        }
    }
}

@Composable
private fun EnvironmentBadge(environment: String, isDark: Boolean) {
    val (bg, fg) = if (environment == "production") {
        Pair(if (isDark) "#450a0a" else "#fee2e2", if (isDark) "#fca5a5" else "#b91c1c")
    } else {
        Pair(if (isDark) "#0c2340" else "#dbeafe", if (isDark) "#93c5fd" else "#1d4ed8")
    }
    Span({
        style { badgeStyle(bg, fg) }
    }) { Text(environment) }
}

@Composable
private fun StatusBadge(status: String, isDark: Boolean) {
    val (bg, fg) = when (status) {
        "passed" -> Pair(if (isDark) "#052e16" else "#d1fae5", if (isDark) "#86efac" else "#065f46")
        "failed" -> Pair(if (isDark) "#450a0a" else "#fee2e2", if (isDark) "#fca5a5" else "#b91c1c")
        "running" -> Pair(if (isDark) "#451a03" else "#fff3cd", if (isDark) "#fcd34d" else "#856404")
        else -> Pair(if (isDark) "#1f1f1f" else "#f3f4f6", if (isDark) "#6b7280" else "#9ca3af")
    }
    Span({
        style { badgeStyle(bg, fg) }
    }) { Text(status) }
}
